import type {
  BeardStyle,
  BodyType,
  BottomStyle,
  CharacterLook,
  HairStyle,
  HatStyle,
  ShoeStyle,
  TopStyle,
} from '../state/types';
import type { IconToken } from '../icons/index';
import type { Random } from '../core/Random';
import { BEARD_STYLES, BOTTOM_STYLES, HAT_STYLES, TOP_STYLES } from '../state/types';

/** True for a woman's build (looks without a body are men's, like every save before there was a choice). */
export const isWoman = (look: Pick<CharacterLook, 'body'>): boolean => look.body === 'woman';

/** The right word for you: `byBody(look, 'king', 'queen')`. */
export const byBody = <T>(look: Pick<CharacterLook, 'body'>, man: T, woman: T): T =>
  isWoman(look) ? woman : man;

export const MEN_NAMES = [
  'Bubba',
  'Cletus',
  'Earl Jr.',
  'Bobby Ray',
  'Billy Bob',
  'Dwayne',
  'Boone',
  'Hoss',
  'Travis',
  'Duane',
] as const;
export const WOMEN_NAMES = [
  'Tammy Jo',
  'Darla',
  'Jolene',
  'Crystal',
  'Lurleen',
  'Brandi',
  'Shawna',
  'Reba Lynn',
  'Destiny',
  'Mavis',
] as const;

export const BODY_LABELS: Record<BodyType, string> = { man: 'Man', woman: 'Woman' };
export const BODY_ICONS = { man: ':man:', woman: ':woman:' } as const satisfies Record<BodyType, IconToken>;

const HAIR_LABELS: Record<HairStyle, string> = {
  mullet: 'Mullet',
  rattail: 'Rattail',
  teased: 'Teased',
  buzz: 'Buzz',
  bald: 'Bald',
  long: 'Long',
  ponytail: 'Ponytail',
  bun: 'Messy bun',
};
const BEARD_LABELS: Record<BeardStyle, string> = {
  none: 'Clean',
  stache: 'Stache',
  goatee: 'Goatee',
  full: 'Full',
};
const TOP_LABELS: Record<TopStyle, [string, string]> = {
  tank: ['Wifebeater', 'Tank top'],
  tee: ['Tee', 'Tee'],
  camo: ['Camo', 'Camo'],
  flannel: ['Flannel', 'Flannel'],
  nascar: ['NASCAR', 'NASCAR'],
  leopard: ['Leopard', 'Leopard'],
  tracksuit: ['Tracksuit', 'Tracksuit'],
  shirtless: ['Shirtless', 'Bikini top'],
  tube: ['Tube top', 'Tube top'],
};
const BOTTOM_LABELS: Record<BottomStyle, [string, string]> = {
  jorts: ['Jorts', 'Daisy Dukes'],
  jeans: ['Jeans', 'Jeans'],
  sweats: ['Sweats', 'Sweats'],
  overalls: ['Overalls', 'Overalls'],
  skirt: ['Denim kilt', 'Denim skirt'],
};
const SHOE_LABELS: Record<ShoeStyle, string> = {
  crocs: 'Crocs',
  boots: 'Boots',
  sneakers: 'Sneakers',
  slippers: 'Slippers',
};
const HAT_LABELS: Record<HatStyle, string> = {
  none: 'No hat',
  trucker: 'Trucker',
  backwards: 'Backwards',
  cowboy: 'Cowboy',
};

/** Names for each style, as the creator, the salon and the lost and found say them. */
export const lookLabel = {
  hair: (v: HairStyle): string => HAIR_LABELS[v],
  beard: (v: BeardStyle): string => BEARD_LABELS[v],
  top: (v: TopStyle, look: Pick<CharacterLook, 'body'>): string => byBody(look, ...TOP_LABELS[v]),
  bottom: (v: BottomStyle, look: Pick<CharacterLook, 'body'>): string => byBody(look, ...BOTTOM_LABELS[v]),
  shoes: (v: ShoeStyle): string => SHOE_LABELS[v],
  hat: (v: HatStyle): string => HAT_LABELS[v],
};

/**
 * Switching between a man's and a woman's build starts from a fresh outfit for that
 * build; skin, hair color and clothing colors carry over.
 */
export function withBody(look: CharacterLook, body: BodyType): CharacterLook {
  if (body === 'woman')
    return {
      ...look,
      body,
      hair: 'ponytail',
      beard: 'none',
      top: 'tank',
      bottom: 'jorts',
      belly: 0.2,
      height: 0.95,
    };
  return {
    ...look,
    body,
    hair: 'mullet',
    beard: 'stache',
    top: 'tank',
    bottom: 'jorts',
    belly: 0.35,
    height: 1,
  };
}

/** The creator's color choices. */
export const LOOK_COLORS = {
  skin: ['#f6d0b1', '#eab08c', '#d9956b', '#b9764d', '#8d5a3b', '#5e3a26'],
  hair: ['#7a4b26', '#2b1d12', '#c9a25c', '#efd98f', '#a33b20', '#9a9a9a', '#ff6fa8', '#7a5cff'],
  cloth: [
    '#f3efe4',
    '#d6452f',
    '#2b6cb0',
    '#ffd400',
    '#2f8f3a',
    '#1d1d1d',
    '#ff6fa8',
    '#7a1f2b',
    '#5e6b3a',
    '#ff7b2e',
  ],
  denim: ['#5f86b8', '#3b5b8c', '#2d3f73', '#1d1d1d', '#8a7a5a', '#5e6b3a', '#c9c2ae', '#ff6fa8'],
  shoes: ['#7ed957', '#ff7b2e', '#ff6fa8', '#3b8bff', '#f2c200', '#5e6b3a', '#1d1d1d', '#f3efe4'],
  hat: ['#c0392b', '#2b6cb0', '#1d1d1d', '#f3efe4', '#2f8f3a', '#5e6b3a', '#8a5a33', '#ffd400'],
} as const;

/** Randomize sticks to looks that suit the build (you can still pick anything by hand). */
const RANDOM_HAIR = {
  man: ['mullet', 'rattail', 'teased', 'buzz', 'bald', 'long', 'ponytail'],
  woman: ['long', 'ponytail', 'bun', 'teased', 'mullet'],
} as const;

export const namesFor = (look: Pick<CharacterLook, 'body'>): readonly string[] =>
  isWoman(look) ? WOMEN_NAMES : MEN_NAMES;

/** A random look for the same build. */
export function randomLook(rng: Random, look: CharacterLook): CharacterLook {
  const woman = isWoman(look);
  return {
    ...look,
    hair: rng.pick(RANDOM_HAIR[woman ? 'woman' : 'man']),
    hairColor: rng.pick(LOOK_COLORS.hair.slice(0, 6)),
    beard: woman ? 'none' : rng.pick(BEARD_STYLES),
    top: rng.pick(TOP_STYLES.filter((t) => woman || t !== 'tube')),
    topColor: rng.pick(LOOK_COLORS.cloth),
    bottom: rng.pick(BOTTOM_STYLES.filter((b) => woman || b !== 'skirt')),
    bottomColor: rng.pick(LOOK_COLORS.denim),
    shoeColor: rng.pick(LOOK_COLORS.shoes),
    hat: rng.pick(HAT_STYLES),
    hatColor: rng.pick(LOOK_COLORS.hat),
    skin: rng.pick(LOOK_COLORS.skin),
    belly: rng.range(0, woman ? 0.6 : 0.9),
    height: woman ? rng.range(0.9, 1) : rng.range(0.94, 1.06),
  };
}
